/** Texts of the session profile model (session volume profile breakout): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const svp = {
  en: {
    "sv.name": "Session Profile",
    "sv.tune": "Tuning",
    "sv.title": "Session Profile tuning",
    "sv.about.title": "Session Profile – the strategy",
    "sv.about.intro": "A breakout of the value area of the session before: London trades against the Asian session's volume profile, New York against London's. A close clearly outside the value area with high volume and enough volatility arms a limit at the area's edge (the retest).",
    "sv.about.steps": "Sessions in London time: Asia 00:00–07:00, London 07:00–13:00, New York 13:00–21:00.\nProfile: each 5M bar spreads its ticks (tick volume — spot gold has no central volume) evenly over the price steps between its low and high, a step 0.01 % of the session's close. POC = the step with the most ticks; the value area grows from the POC by the larger neighbouring step until it holds 70 % of the ticks; VAH / VAL its upper / lower edge.\nBreakout: a 5M bar of the session closes at least 0.1 ATR above the VAH (buy) or below the VAL (sell); its ticks at least the tuning's multiple of the mean of the 20 bars before; the ATR(14) at least 0.8 × its mean over the day before (288 bars).\nRetest: a limit at the VAH (VAL), valid 12 bars after the breakout; void once a bar closes more than 0.5 ATR back inside the value area.\nStop: the tuning's ATR (of the breakout bar) beyond the VAH (VAL). Target: the tuning's R. Flat at the session's end at the latest.\nOne setup per session and side; one position at a time.\nZones: from the VAH up (VAL down) 0.1 ATR — where the breakout closes and the limit waits.",
    "sv.about.evidence": "Measured (round 45, gold 5M 2010 – 10/2026, bid/ask, $0.07 commission, $0.10 slippage, PREREG45): negative after costs in every setting. Chosen setting (1.5 R, stop 1.25 ATR, volume × 1.5): out of sample 2023–2026 −0.19 R per trade (n 1,086, profit factor 0.72), before costs +0.07 R; 2010–2026 −0.41 R. No edge — discarded. Shown for analysis only.",
    "sv.atr": "{n} ATR",
    "sv.r": "{n} R",
    "sv.times": "{n} ×",
    "sv.stop": "Stop beyond the edge",
    "sv.target": "Target",
    "sv.volume": "Breakout volume",
    "sv.preset.1": "Standard",
    "sv.preset.2": "Tight stop, 3 R",
    "sv.preset.3": "Average volume, 1.5 R",
    "sv.frames": "5M · session profile",
    "sv.session.asia": "Asia",
    "sv.session.london": "London",
    "sv.session.newyork": "New York",
    "sv.session.against": "{name} · against the {ref} profile",
    "sv.session.building": "{name} · its profile builds up",
    "sv.session.outside": "outside the sessions",
    "sv.row.session": "Session",
    "sv.row.profile": "Profile",
    "sv.row.profileValues": "VAH {vah} · POC {poc} · VAL {val}",
    "sv.row.none": "none",
    "sv.role.active": "active",
    "sv.role.next": "next",
    "sv.level": "{edge} · {ref} profile",
    "sv.step.breakout": "Breakout",
    "sv.step.breakoutHow.long": "close ≥ 0.1 ATR above VAH",
    "sv.step.breakoutHow.short": "close ≥ 0.1 ATR below VAL",
    "sv.step.retest": "Retest",
    "sv.step.retestUntil": "limit until {time}",
    "sv.step.expired": "expired",
    "sv.step.cancelled": "void",
    "sv.step.entry": "Entry",
    "sv.step.open": "running",
    "sv.step.target": "target reached",
    "sv.step.stop": "stopped",
    "sv.step.flat": "flat at the session's end",
    "sv.tip.noProfile": "No profile to trade against: trades only in London and New York.",
    "sv.tip.wait.long": "Waiting for a close above {price} (VAH + 0.1 ATR).",
    "sv.tip.wait.short": "Waiting for a close below {price} (VAL − 0.1 ATR).",
    "sv.tip.pending.long": "Buy limit {entry} · stop {stop} · target {target}",
    "sv.tip.pending.short": "Sell limit {entry} · stop {stop} · target {target}",
    "sv.tip.open.long": "Long since {time}",
    "sv.tip.open.short": "Short since {time}",
    "sv.tip.done": "This session's setup is over — no further one on this side.",
    "sv.fig.limit": "Limit",
    "sv.fig.entry": "Entry",
    "sv.fig.stop": "Stop",
    "sv.fig.target": "Target {r} R",
    "sv.chart.vah": "VAH {ref}",
    "sv.chart.val": "VAL {ref}",
    "sv.chart.poc": "POC {ref}",
    "sv.chart.limit": "Limit {arrow}",
    "sv.chart.entry": "Entry {arrow}",
    "sv.chart.stop": "Stop",
    "sv.chart.target": "Target",
    "sv.chart.breakout": "Breakout {arrow}",
    "sv.chart.breakoutHint": "Session Profile: the close broke out of the value area of the session before with high volume.",
    "sv.zone.long": "BUY ZONE {price} · VAH",
    "sv.zone.short": "SELL ZONE {price} · VAL",
    "sv.legend": "Zone: from the edge of the value area 0.1 ATR outward — where the breakout closes and the limit waits.",
    "chk.svpProfile": "Profile to trade against",
    "chk.svpProfile.hint": "Session Profile. ✓ in London (against the Asian profile) and New York (against the London profile of the same day).",
    "chk.svpBreak": "Close beyond the edge",
    "chk.svpBreak.hint": "Session Profile. ✓ buy when the last close lies at least 0.1 ATR above the VAH; ✓ sell at least 0.1 ATR below the VAL.",
    "chk.svpVolume": "Volume",
    "chk.svpVolume.hint": "Session Profile. ✓ when the last bar's ticks reach the tuning's multiple of the mean of the 20 bars before.",
    "chk.svpAtr": "Volatility",
    "chk.svpAtr.hint": "Session Profile. ✓ when the ATR(14) is at least 0.8 × its mean over the 288 bars (one day) before.",
    "chk.svpSetup": "Setup",
    "chk.svpSetup.hint": "Session Profile. ✓ on its side while the limit at the VAH (VAL) waits or its trade runs.",
  },
  de: {
    "sv.name": "Session-Profil",
    "sv.tune": "Feintuning",
    "sv.title": "Session-Profil-Feintuning",
    "sv.about.title": "Session-Profil – die Strategie",
    "sv.about.intro": "Ein Ausbruch aus der Value Area der Sitzung davor: London handelt gegen das Volumenprofil der Asien-Sitzung, New York gegen das von London. Ein Schluss klar außerhalb der Value Area mit hohem Volumen und genug Schwankung setzt ein Limit an der Kante der Area (der Retest).",
    "sv.about.steps": "Sitzungen in Londoner Zeit: Asien 00:00–07:00, London 07:00–13:00, New York 13:00–21:00.\nProfil: Jede 5M-Kerze verteilt ihre Ticks (Tick-Volumen — Spot-Gold hat kein zentrales Volumen) gleich auf die Preisstufen zwischen Tief und Hoch, eine Stufe 0,01 % des Sitzungsschlusses. POC = Stufe mit den meisten Ticks; die Value Area wächst vom POC aus um die jeweils größere Nachbarstufe, bis sie 70 % der Ticks hält; VAH / VAL ihre obere / untere Kante.\nAusbruch: Eine 5M-Kerze der Sitzung schließt mindestens 0,1 ATR über dem VAH (Kauf) bzw. unter dem VAL (Verkauf); ihre Ticks mindestens das Vielfache aus dem Feintuning vom Mittel der 20 Kerzen davor; der ATR(14) mindestens 0,8 × sein Mittel über den Tag davor (288 Kerzen).\nRetest: ein Limit am VAH (VAL), gültig 12 Kerzen nach dem Ausbruch; ungültig, sobald eine Kerze mehr als 0,5 ATR zurück in der Value Area schließt.\nStop: die ATR aus dem Feintuning (der Ausbruchskerze) jenseits des VAH (VAL). Ziel: die R aus dem Feintuning. Spätestens zum Ende der Sitzung flach.\nJe Sitzung und Seite ein Setup; immer nur eine Position.\nZonen: vom VAH aufwärts (VAL abwärts) 0,1 ATR — dort schließt der Ausbruch und wartet das Limit.",
    "sv.about.evidence": "Gemessen (Runde 45, Gold 5M 2010 – 10/2026, Geld/Brief, 0,07 $ Kommission, 0,10 $ Slippage, PREREG45): in jeder Einstellung nach Kosten negativ. Gewählte Einstellung (1,5 R, Stop 1,25 ATR, Volumen × 1,5): außerhalb 2023–2026 −0,19 R je Trade (n 1.086, Profit-Faktor 0,72), vor Kosten +0,07 R; 2010–2026 −0,41 R. Kein Vorteil – verworfen. Nur zur Analyse.",
    "sv.atr": "{n} ATR",
    "sv.r": "{n} R",
    "sv.times": "{n} ×",
    "sv.stop": "Stop jenseits der Kante",
    "sv.target": "Ziel",
    "sv.volume": "Volumen des Ausbruchs",
    "sv.preset.1": "Standard",
    "sv.preset.2": "Enger Stop, 3 R",
    "sv.preset.3": "Mittleres Volumen, 1,5 R",
    "sv.frames": "5M · Sitzungsprofil",
    "sv.session.asia": "Asien",
    "sv.session.london": "London",
    "sv.session.newyork": "New York",
    "sv.session.against": "{name} · gegen das {ref}-Profil",
    "sv.session.building": "{name} · ihr Profil entsteht",
    "sv.session.outside": "außerhalb der Sitzungen",
    "sv.row.session": "Sitzung",
    "sv.row.profile": "Profil",
    "sv.row.profileValues": "VAH {vah} · POC {poc} · VAL {val}",
    "sv.row.none": "keins",
    "sv.role.active": "aktiv",
    "sv.role.next": "nächste",
    "sv.level": "{edge} · {ref}-Profil",
    "sv.step.breakout": "Ausbruch",
    "sv.step.breakoutHow.long": "Schluss ≥ 0,1 ATR über VAH",
    "sv.step.breakoutHow.short": "Schluss ≥ 0,1 ATR unter VAL",
    "sv.step.retest": "Retest",
    "sv.step.retestUntil": "Limit bis {time}",
    "sv.step.expired": "verfallen",
    "sv.step.cancelled": "ungültig",
    "sv.step.entry": "Einstieg",
    "sv.step.open": "läuft",
    "sv.step.target": "Ziel erreicht",
    "sv.step.stop": "ausgestoppt",
    "sv.step.flat": "zum Sitzungsende glattgestellt",
    "sv.tip.noProfile": "Kein Profil zum Handeln: gehandelt wird nur in London und New York.",
    "sv.tip.wait.long": "Wartet auf einen Schluss über {price} (VAH + 0,1 ATR).",
    "sv.tip.wait.short": "Wartet auf einen Schluss unter {price} (VAL − 0,1 ATR).",
    "sv.tip.pending.long": "Kauf-Limit {entry} · Stop {stop} · Ziel {target}",
    "sv.tip.pending.short": "Verkaufs-Limit {entry} · Stop {stop} · Ziel {target}",
    "sv.tip.open.long": "Long seit {time}",
    "sv.tip.open.short": "Short seit {time}",
    "sv.tip.done": "Das Setup dieser Sitzung ist vorbei — auf dieser Seite keins mehr.",
    "sv.fig.limit": "Limit",
    "sv.fig.entry": "Einstieg",
    "sv.fig.stop": "Stop",
    "sv.fig.target": "Ziel {r} R",
    "sv.chart.vah": "VAH {ref}",
    "sv.chart.val": "VAL {ref}",
    "sv.chart.poc": "POC {ref}",
    "sv.chart.limit": "Limit {arrow}",
    "sv.chart.entry": "Einstieg {arrow}",
    "sv.chart.stop": "Stop",
    "sv.chart.target": "Ziel",
    "sv.chart.breakout": "Ausbruch {arrow}",
    "sv.chart.breakoutHint": "Session-Profil: Der Schluss brach mit hohem Volumen aus der Value Area der Sitzung davor aus.",
    "sv.zone.long": "BUY ZONE {price} · VAH",
    "sv.zone.short": "SELL ZONE {price} · VAL",
    "sv.legend": "Zone: von der Kante der Value Area 0,1 ATR nach außen — dort schließt der Ausbruch und wartet das Limit.",
    "chk.svpProfile": "Profil zum Handeln",
    "chk.svpProfile.hint": "Session-Profil. ✓ in London (gegen das Asien-Profil) und New York (gegen das London-Profil desselben Tages).",
    "chk.svpBreak": "Schluss jenseits der Kante",
    "chk.svpBreak.hint": "Session-Profil. ✓ Kauf, wenn der letzte Schluss mindestens 0,1 ATR über dem VAH liegt; ✓ Verkauf mindestens 0,1 ATR unter dem VAL.",
    "chk.svpVolume": "Volumen",
    "chk.svpVolume.hint": "Session-Profil. ✓, wenn die Ticks der letzten Kerze das Vielfache aus dem Feintuning vom Mittel der 20 Kerzen davor erreichen.",
    "chk.svpAtr": "Schwankung",
    "chk.svpAtr.hint": "Session-Profil. ✓, wenn der ATR(14) mindestens 0,8 × sein Mittel über die 288 Kerzen (einen Tag) davor ist.",
    "chk.svpSetup": "Setup",
    "chk.svpSetup.hint": "Session-Profil. ✓ auf seiner Seite, solange das Limit am VAH (VAL) wartet oder sein Trade läuft.",
  },
};
